import { useState } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { cx } from '@emotion/css';
import { Autocomplete, Dashed } from '@/components';

import { Close as CloseIcon } from '@mui/icons-material';

import infoIcon from '@/assets/info-icon.svg';
import { useGoogleTranslator } from '@/hooks/useGoogleTranslator';
import {
  BodySmall,
  BodyMediumMidEmphasis,
  ButtonFlatSmall,
  ButtonPrimarySmall,
  BodyLarge,
} from '@/design-system';
import colors from '@/theme/colors';
import { SelectInput } from '../SelectInput/SelectInput';
import ChildRemovalConfirmation from '../ChildRemovalConfirmation/ChildRemovalConfirmation';
import styles from './ChildEnrollment.styles';

const toolTipText =
  'Enter the school your child or children attend. Only schools that are using Peachjar are available for selection.';

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;

type SchoolItem = {
  description: string;
  schoolId: number;
  schoolName: string;
  districtId: number;
  districtName: string;
  gradeLevels: string;
};

const ChildEnrollment = ({
  id,
  childUid,
  disabled,
  wasAdded,
  wasChanged,
  institution,
  currentGrade,
  grades,
  lookUpSchools,
  gradeLevelChanged,
  handleSubmit,
  handleSchoolChange,
  handleGradeChange,
  handleRemoveChild,
  handleCancel,
}) => {
  const [isRemovalModalOpen, setIsRemovalModalOpen] = useState<boolean>(false);
  const [hasSchoolError, setHasSchoolError] = useState<boolean>(false);
  const [hasGradeError, setHasGradeError] = useState<boolean>(false);
  const toolTipTextTranslated = useGoogleTranslator(toolTipText);
  const schoolNameLabel = useGoogleTranslator('School Name');
  const gradeLabel = useGoogleTranslator(`Grade (${currentYear}/${nextYear} school year)`);
  const isFormInvalid = institution.institutionName === '' || !currentGrade;

  const onClickButtonSubmit = () => {
    if (institution.institutionName === '') {
      setHasSchoolError(true);
    }
    if (!currentGrade) {
      setHasGradeError(true);
    }
    if (isFormInvalid) {
      return;
    }

    handleSubmit(childUid);
  };

  return (
    <>
      <div className={styles.cardBorder} />
      <div
        className={cx(
          styles.enrollmentGroup,
          hasSchoolError || hasGradeError ? styles.hasError : {}
        )}
        data-testid="childCard"
      >
        <div className={cx(styles.inputWithToolTip, styles.inlined)}>
          <div className={styles.formSectionHeadline}>
            <BodyMediumMidEmphasis>Child</BodyMediumMidEmphasis>
          </div>
          {disabled ? (
            <div>
              <BodySmall className={styles.label} color={colors.slate}>
                School Name
              </BodySmall>
              <BodyLarge
                className={styles.textInput}
                data-testid="schoolNameLabel"
                color={colors.prussian}
              >
                {institution.institutionName}
              </BodyLarge>
            </div>
          ) : (
            <div className={cx(styles.autocomplete, hasSchoolError && styles.inputSchoolError)}>
              <Autocomplete<SchoolItem>
                id={`schoolInput${id}`}
                lookup={lookUpSchools}
                label={schoolNameLabel}
                value={institution.institutionName}
                isEqual={(s1, s2) => {
                  return (s1 && s2 && s1.schoolId === s2.schoolId) || false;
                }}
                onChange={val => {
                  if (val) {
                    setHasSchoolError(false);
                  }
                  handleSchoolChange(val, childUid);
                }}
                renderOption={val => (
                  <div data-testid="schoolNameOption">
                    {val.schoolName}
                    <span className={styles.secondaryName} data-testid="districtNameOption">
                      {val.districtName}
                    </span>
                  </div>
                )}
                error={hasSchoolError ? 'School required' : null}
                data-testid="schoolAutocompleteInput"
              />
            </div>
          )}

          {disabled ? (
            <div className={styles.labelGrade}>
              <BodySmall color={colors.slate} className={styles.label} data-testid="gradeLabel">
                {gradeLabel}
              </BodySmall>
              <BodyLarge color={colors.slate} className={styles.textInput}>
                {!currentGrade || currentGrade.description === 'N' ? (
                  <Dashed data-testid="grade" />
                ) : (
                  <span data-testid="grade">{currentGrade.description}</span>
                )}
              </BodyLarge>
            </div>
          ) : (
            <div
              className={cx(
                styles.inputWithToolTip,
                styles.enrollmentGroup,
                styles.gradeOptions,
                hasGradeError && styles.inputGradeError
              )}
            >
              <SelectInput
                childUid={childUid}
                value={currentGrade}
                label={gradeLabel}
                onChange={input => {
                  setHasGradeError(false);
                  handleGradeChange(input, childUid);
                }}
                options={grades}
                error={hasGradeError}
              />
              {hasGradeError && <BodySmall color={colors.dragon}>Grade required</BodySmall>}
            </div>
          )}

          {!disabled && (
            <div className={`${styles.removeIcon} ${styles.tooltipButton}`}>
              <Tooltip
                title="Remove Child"
                placement="right"
                enterTouchDelay={0}
                data-testid="removeChildTooltip"
              >
                <IconButton
                  aria-label="Remove Child"
                  data-testid="removeChildButton"
                  onClick={() => {
                    if (wasAdded) {
                      handleRemoveChild(childUid);
                    } else {
                      setIsRemovalModalOpen(true);
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
        {gradeLevelChanged && (
          <span className={`${styles.infoIcon}`} data-testid="gradeLevelChangedIcon">
            <img src={infoIcon} alt="Information icon" />
          </span>
        )}

        {(wasAdded || wasChanged) && (
          <div className={styles.footer}>
            <div>
              <ButtonFlatSmall
                onClick={() => handleCancel(childUid)}
                data-testid="childCardCancelButton"
              >
                Cancel
              </ButtonFlatSmall>
            </div>
            <div onClick={onClickButtonSubmit}>
              <ButtonPrimarySmall disabled={isFormInvalid} data-testid="childCardSubmitButton">
                Save
              </ButtonPrimarySmall>
            </div>
          </div>
        )}
      </div>
      <ChildRemovalConfirmation
        isOpen={isRemovalModalOpen}
        schoolName={institution.institutionName}
        onSubmit={() => {
          setIsRemovalModalOpen(false);
          handleRemoveChild(childUid);
        }}
        onCancel={() => {
          setIsRemovalModalOpen(false);
        }}
      />
    </>
  );
};

export default ChildEnrollment;
